import React, { forwardRef, useState } from 'react';
import {
  any,
  bool,
  func,
  node,
  number,
  oneOf,
  shape,
  string
} from 'prop-types';
import ClassNames from 'classnames';

import './Input.scss';

const AUTOCOMPLETE_VALUES = ['on', 'off'];
const INPUT_TYPE_VALUES = ['email', 'number', 'password', 'tel', 'text'];

const Input = forwardRef(
  (
    {
      autoComplete,
      className,
      defaultValue,
      disabled,
      id,
      invalid,
      maxLength,
      multiple,
      name,
      onBlur,
      onChange,
      onClick,
      onFocus,
      onKeyUp,
      placeholder,
      prefix,
      readOnly,
      style,
      suffix,
      tabIndex,
      type,
      value,
      wrapperClassName
    },
    ref
  ) => {
    const [focus, setFocus] = useState(false);

    const handleBlur = event => {
      if (onBlur) {
        onBlur(event);
      }

      setFocus(false);
    };

    const handleFocus = event => {
      if (onFocus) {
        onFocus(event);
      }

      setFocus(true);
    };

    return (
      // Use a label element as the container so that clicks will focus the
      // input.
      <label
        className={ClassNames(
          'input__wrapper',
          wrapperClassName,
          focus && 'input__wrapper--focus',
          invalid && 'input__wrapper--invalid',
          disabled && 'input__wrapper--disabled',
          readOnly && 'input__wrapper--read-only'
        )}
        htmlFor={id}
      >
        {!!prefix && <span className="input__prefix">{prefix}</span>}
        <input
          autoComplete={autoComplete}
          aria-invalid={!!invalid}
          className={ClassNames('input', className)}
          defaultValue={defaultValue}
          disabled={disabled}
          ref={ref}
          id={id}
          maxLength={maxLength}
          multiple={multiple}
          name={name}
          onBlur={handleBlur}
          onClick={onClick}
          onChange={onChange}
          onFocus={handleFocus}
          onKeyUp={onKeyUp}
          placeholder={placeholder}
          readOnly={readOnly}
          style={style}
          tabIndex={tabIndex}
          type={type}
          value={value}
        />
        {!!suffix && <span className="input__suffix">{suffix}</span>}
      </label>
    );
  }
);

Input.propTypes = {
  autoComplete: oneOf(AUTOCOMPLETE_VALUES),
  className: string,
  defaultValue: string,
  disabled: bool,
  id: string,
  // Allow any because we're going to convert the value to a boolean anyway.
  // eslint-disable-next-line react/forbid-prop-types
  invalid: any,
  maxLength: number,
  multiple: bool,
  name: string,
  onBlur: func,
  onChange: func,
  onClick: func,
  onFocus: func,
  onKeyUp: func,
  placeholder: string,
  prefix: node,
  readOnly: bool,
  style: shape({}),
  suffix: node,
  tabIndex: number,
  type: oneOf(INPUT_TYPE_VALUES),
  value: string,
  wrapperClassName: string
};

Input.defaultProps = {
  autoComplete: undefined,
  className: undefined,
  defaultValue: undefined,
  disabled: undefined,
  id: undefined,
  invalid: false,
  maxLength: undefined,
  multiple: undefined,
  name: '',
  onBlur: undefined,
  onChange: undefined,
  onClick: undefined,
  onFocus: undefined,
  onKeyUp: undefined,
  placeholder: undefined,
  prefix: undefined,
  readOnly: undefined,
  style: undefined,
  suffix: undefined,
  tabIndex: undefined,
  type: 'text',
  value: undefined,
  wrapperClassName: undefined
};

export default Input;
